import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Plus, Trash, CaretLeft, CaretRight, Check, Fire, MagicWand } from "@phosphor-icons/react";

const pad = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const COLORS = {
  emerald: "#10b981", blue: "#3b82f6", pink: "#ec4899", amber: "#f59e0b",
  violet: "#8b5cf6", red: "#ef4444", cyan: "#06b6d4", lime: "#84cc16",
};
const habitColor = (h) => COLORS[h.color] || COLORS.emerald;

export default function Planner() {
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [newName, setNewName] = useState("");
  const [newGoal, setNewGoal] = useState(30);
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [applying, setApplying] = useState(null);

  useEffect(() => { api.get("/habits/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const applyTemplate = async (t) => {
    setApplying(t.id);
    try {
      const r = await api.post(`/habits/template/${t.id}`);
      toast.success(r.data.created > 0
        ? `Добавлено привычек: ${r.data.created}`
        : "Все привычки шаблона уже есть в таблице");
      setTplOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setApplying(null);
  };

  const load = () => api.get(`/habits/tracker?month=${ym}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [ym]);

  const [y, m] = ym.split("-").map(Number);
  const dim = new Date(y, m, 0).getDate();
  const todayStr = localISO(new Date());

  const weeks = useMemo(() => {
    const out = [];
    let cur = [];
    for (let d = 1; d <= dim; d++) {
      if (new Date(y, m - 1, d).getDay() === 1 && cur.length) { out.push(cur); cur = []; }
      cur.push(d);
    }
    if (cur.length) out.push(cur);
    return out;
  }, [ym]);

  const fmtDM = (d) => new Date(y, m - 1, d).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
  const rangeLabel = (w) => (w.length === 1 ? fmtDM(w[0]) : `${w[0]}–${fmtDM(w[w.length - 1])}`);
  const fmtFull = (ds) => new Date(ds + "T00:00:00").toLocaleDateString("ru-RU", { weekday: "short", day: "numeric", month: "long" });

  const monthTitle = useMemo(() => {
    const s = new Date(y, m - 1, 1).toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  }, [ym]);

  const shift = (d) => {
    const dt = new Date(y, m - 1 + d, 1);
    setYm(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}`);
  };

  const toggle = (h, ds) => {
    setRows((rs) => rs.map((r) => r.id === h.id
      ? { ...r, days: r.days.includes(ds) ? r.days.filter((x) => x !== ds) : [...r.days, ds] }
      : r));
    api.post(`/habits/${h.id}/toggle`, { date: ds }).catch(() => load());
  };

  const saveEdit = async () => {
    if (!edit) return;
    const e = edit;
    setEdit(null);
    try {
      if (e.field === "name") {
        if (!e.value.trim()) return;
        await api.patch(`/habits/${e.id}`, { name: e.value.trim() });
      } else {
        const g = Math.max(1, Math.min(366, parseInt(e.value) || 0));
        await api.patch(`/habits/${e.id}`, { goal_days: g });
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/habits", { name: newName.trim(), goal_days: Math.max(1, Math.min(366, parseInt(newGoal) || 30)) });
      setNewName("");
      toast.success("Привычка добавлена");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setColor = (h, c) => {
    setRows((rs) => rs.map((r) => (r.id === h.id ? { ...r, color: c } : r)));
    api.patch(`/habits/${h.id}`, { color: c }).catch(() => load());
  };

  const remove = async (id) => {
    await api.delete(`/habits/${id}`);
    toast.success("Привычка удалена");
    load();
  };

  const doneTotal = rows ? rows.reduce((s, h) => s + h.days.length, 0) : 0;
  const target = rows ? rows.reduce((s, h) => s + Math.min(h.goal_days || 30, dim), 0) : 0;
  const left = Math.max(0, target - doneTotal);
  const pct = target ? Math.round((doneTotal / target) * 100) : 0;

  const editCell = (h, field, cls, testid) => (
    edit?.id === h.id && edit.field === field ? (
      <input
        autoFocus
        type={field === "goal" ? "number" : "text"}
        value={edit.value}
        onChange={(e) => setEdit((ed) => ({ ...ed, value: e.target.value }))}
        onBlur={saveEdit}
        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEdit(null); }}
        className={`bg-transparent border-b border-primary outline-none ${cls}`}
        data-testid={`${testid}-input`}
      />
    ) : (
      <button
        onClick={() => setEdit({ id: h.id, field, value: field === "goal" ? String(h.goal_days) : h.name })}
        data-testid={testid}
        title="Нажмите, чтобы изменить"
        className={`hover:text-primary transition-colors ${cls}`}
      >
        {field === "goal" ? h.goal_days : h.name}
      </button>
    )
  );

  return (
    <div className="fade-in" data-testid="planner-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Трекер месяца</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Планнер</h1>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={tplOpen} onOpenChange={setTplOpen}>
            <DialogTrigger asChild>
              <button data-testid="templates-button"
                className="flex items-center gap-1.5 glass-card px-3 py-2.5 text-sm font-medium hover:text-primary transition-colors">
                <MagicWand size={16} weight="duotone" /> Шаблоны
              </button>
            </DialogTrigger>
            <DialogContent className="glass-modal max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Шаблоны челленджей</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {templates.map((t) => (
                  <div key={t.id} className="rounded-xl border border-border/60 p-3.5" data-testid={`template-${t.id}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="font-heading font-bold">{t.title}</span>
                      <span className="text-xs text-muted-foreground num whitespace-nowrap">{t.days} дней</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{t.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {t.habits.map((h) => (
                        <span key={h.name} className="flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full bg-secondary">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[h.color] }} />
                          {h.name}
                        </span>
                      ))}
                    </div>
                    <Button size="sm" className="w-full" disabled={applying === t.id}
                      onClick={() => applyTemplate(t)} data-testid={`template-apply-${t.id}`}>
                      {applying === t.id ? "Добавляем…" : "Добавить в таблицу"}
                    </Button>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          <div className="flex items-center gap-1 glass-card px-2 py-1.5">
            <button onClick={() => shift(-1)} data-testid="planner-month-prev" className="p-1.5 rounded-md hover:bg-secondary transition-colors"><CaretLeft size={16} /></button>
            <span className="text-sm font-semibold min-w-[130px] text-center" data-testid="planner-month-title">{monthTitle}</span>
            <button onClick={() => shift(1)} data-testid="planner-month-next" className="p-1.5 rounded-md hover:bg-secondary transition-colors"><CaretRight size={16} /></button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] gap-4 sm:gap-5 mb-5 stagger">
        <div className="glass-card overflow-hidden" data-testid="progress-overview">
          <div className="px-4 py-2.5 bg-primary/15 text-sm font-semibold font-heading">Обзор прогресса</div>
          <div className="p-4 flex items-center gap-4">
            <div className="w-24 h-24 min-w-[96px] min-h-[96px] relative shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={[{ v: doneTotal }, { v: left || (doneTotal ? 0 : 1) }]} dataKey="v" innerRadius={30} outerRadius={44} startAngle={90} endAngle={-270} stroke="none">
                    <Cell fill="hsl(var(--primary))" />
                    <Cell fill="hsl(var(--secondary))" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold num" data-testid="progress-pct">{pct}%</span>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2 whitespace-nowrap"><span className="w-2.5 h-2.5 rounded-sm bg-primary shrink-0" /> Выполнено <b className="num">{doneTotal}</b></div>
              <div className="flex items-center gap-2 whitespace-nowrap"><span className="w-2.5 h-2.5 rounded-sm bg-secondary border border-border shrink-0" /> Осталось <b className="num">{left}</b></div>
            </div>
          </div>
        </div>
        <form onSubmit={add} className="glass-card p-4 self-center w-full flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5">Новая привычка</label>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} required placeholder="Например: 10 тыс шагов"
              data-testid="tracker-add-input"
              className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus:border-primary transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5">Цель в днях</label>
            <input type="number" min={1} max={366} value={newGoal} onChange={(e) => setNewGoal(e.target.value)}
              data-testid="tracker-add-goal"
              className="w-24 h-10 rounded-md border border-input bg-transparent px-3 text-sm num outline-none focus:border-primary transition-colors" />
          </div>
          <Button type="submit" data-testid="tracker-add-submit" className="active:scale-95 transition-transform h-10">
            <Plus size={16} className="mr-1" /> Добавить
          </Button>
        </form>
      </div>

      {rows === null ? (
        <div className="glass-card h-64 animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="glass-card p-12 text-center text-muted-foreground" data-testid="planner-empty">
          Пока нет привычек — добавьте первую строку таблицы выше!
        </div>
      ) : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-sm" data-testid="tracker-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                <th rowSpan={2} className="sticky left-0 z-10 bg-card/95 backdrop-blur-sm text-left px-3 py-2 min-w-[150px] max-w-[220px] border-b border-r border-border/60 font-semibold normal-case tracking-normal text-sm text-foreground">
                  Повседневные привычки
                </th>
                <th rowSpan={2} className="px-2 py-2 border-b border-r border-border/60 whitespace-nowrap font-medium">Цели<br />в днях</th>
                {weeks.map((w, i) => (
                  <th key={i} colSpan={w.length} className={`px-2 py-1.5 border-b border-r border-border/60 font-semibold ${i % 2 ? "bg-primary/[0.13]" : "bg-primary/[0.06]"}`}>
                    неделя {i + 1}
                    <span className="block text-[10px] font-normal normal-case tracking-normal text-muted-foreground whitespace-nowrap" data-testid={`week-range-${i + 1}`}>
                      {rangeLabel(w)}
                    </span>
                  </th>
                ))}
                <th rowSpan={2} className="px-2 py-2 border-b border-border/60 font-medium">Итог</th>
                <th rowSpan={2} className="px-1 py-2 border-b border-border/60 w-8" />
              </tr>
              <tr className="text-[11px] text-muted-foreground">
                {weeks.map((w, wi) => w.map((d, di) => {
                  const ds = `${ym}-${pad(d)}`;
                  return (
                    <th key={d} title={fmtFull(ds)} className={`px-0 py-1 num font-medium min-w-[26px] border-b border-border/60 ${di === w.length - 1 ? "border-r" : ""} ${wi % 2 ? "bg-primary/[0.13]" : "bg-primary/[0.06]"} ${ds === todayStr ? "text-primary font-bold" : ""}`}>
                      {d}
                    </th>
                  );
                }))}
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr key={h.id} className="group hover:bg-secondary/30 transition-colors" data-testid={`tracker-row-${h.id}`}>
                  <td className="sticky left-0 z-10 bg-card/95 backdrop-blur-sm px-3 py-1.5 border-b border-r border-border/40 max-w-[220px]">
                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button data-testid={`tracker-color-${h.id}`} title="Цвет привычки"
                            className="w-3.5 h-3.5 rounded-full shrink-0 ring-1 ring-inset ring-black/10 hover:scale-125 transition-transform"
                            style={{ backgroundColor: habitColor(h) }} />
                        </PopoverTrigger>
                        <PopoverContent side="right" className="glass-modal w-auto p-2 flex gap-1.5">
                          {Object.entries(COLORS).map(([key, hex]) => (
                            <button key={key} onClick={() => setColor(h, key)} data-testid={`color-opt-${key}-${h.id}`}
                              className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${h.color === key ? "ring-2 ring-foreground/60 ring-offset-1 ring-offset-background" : ""}`}
                              style={{ backgroundColor: hex }} />
                          ))}
                        </PopoverContent>
                      </Popover>
                      {editCell(h, "name", "flex-1 text-left text-sm truncate block min-w-0", `tracker-name-${h.id}`)}
                      {h.streak >= 2 && (
                        <span data-testid={`tracker-streak-${h.id}`} title={`Серия без пропусков: ${h.streak} дн.`}
                          className={`flex items-center gap-0.5 text-[11px] num font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                            h.streak >= 7
                              ? "bg-orange-500/20 text-orange-400 drop-shadow-[0_0_6px_rgba(251,146,60,0.8)]"
                              : "bg-amber-500/15 text-amber-500"
                          }`}>
                          <Fire size={11} weight="fill" />{h.streak}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-center border-b border-r border-border/40">
                    {editCell(h, "goal", "w-12 text-center num text-sm", `tracker-goal-${h.id}`)}
                  </td>
                  {weeks.map((w, wi) => w.map((d, di) => {
                    const ds = `${ym}-${pad(d)}`;
                    const done = h.days.includes(ds);
                    const future = ds > todayStr;
                    return (
                      <td key={ds} className={`px-0.5 py-1.5 text-center border-b border-border/40 ${di === w.length - 1 ? "border-r" : ""} ${wi % 2 ? "bg-primary/[0.07]" : "bg-primary/[0.03]"}`}>
                        <button
                          disabled={future}
                          onClick={() => toggle(h, ds)}
                          data-testid={`tracker-cell-${h.id}-${ds}`}
                          title={fmtFull(ds)}
                          style={done ? { backgroundColor: habitColor(h), borderColor: habitColor(h) } : undefined}
                          className={`w-[18px] h-[18px] mx-auto rounded-[5px] border flex items-center justify-center transition-all duration-150 ${
                            done ? "text-white scale-105" : "border-border bg-background/40 hover:border-primary"
                          } ${future ? "opacity-25 cursor-not-allowed" : "active:scale-90"}`}
                        >
                          {done && <Check size={11} weight="bold" />}
                        </button>
                      </td>
                    );
                  }))}
                  <td className="px-2 py-1.5 text-center num font-semibold border-b border-border/40" data-testid={`tracker-total-${h.id}`}>
                    <span style={{ color: habitColor(h) }}>{h.days.length}</span><span className="text-muted-foreground font-normal">/{h.goal_days}</span>
                  </td>
                  <td className="px-1 py-1.5 text-center border-b border-border/40">
                    <button onClick={() => remove(h.id)} data-testid={`tracker-delete-${h.id}`}
                      className="text-muted-foreground/40 group-hover:text-muted-foreground hover:!text-destructive transition-colors">
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-2 text-[11px] text-muted-foreground">Нажмите на название или цель, чтобы изменить. Точка слева — цвет привычки. Будущие дни недоступны.</p>
        </div>
      )}
    </div>
  );
}
